import { Dialog, DialogActions, DialogContent, DialogTitle, Divider, Stack, Typography } from '@mui/material';
import { getLinkedinLocationsWithCookies } from 'API/geo-urn';
import { getJobTitleSuggestions } from 'API/job-title-suggestions';
import PaiLink from 'features/ui/base-components/pai-link';
import { useUserData } from 'hooks/use-user-data';
import i18n from 'i18n';
import _ from 'lodash';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useErrorStore from 'stores/error-store';
import { makeStyles } from 'tss-react/mui';
import PaiButton from '../../ui/base-components/pai-button';
import PaiCheckbox from '../../ui/base-components/pai-checkbox';
import PaiDotLoader from '../../ui/base-components/pai-dot-loader';
import PaiTextField from '../../ui/base-components/pai-text-field';
import JobTitlesTable from './job-titles-table';

interface Props {
  open: boolean;
  occupation: string;
  text: string;

  liAtCookie: string;
  sessionCookie: string;

  handleClose(): void;
}

export interface JobTitle {
  title: string;
  linkedinFreq: number;
}

const JobTitleSuggestionsDialog: React.FC<Props> = ({ open, occupation, text, liAtCookie, sessionCookie, handleClose }) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();
  useUserData();

  const [locationSearch, setLocationSearch] = useState('');
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [locations, setLocations] = useState<string[][]>([]);

  const [jobTitlesLoading, setJobTitlesLoading] = useState(false);
  const [jobTitles, setJobTitles] = useState<JobTitle[]>([]);

  const [selectedLocations, setSelectedLocations] = useState<string[][]>([]);
  const selectedLocationURNs = selectedLocations.map((item) => item[0]);
  const filteredLocations = locations.filter((location) => !selectedLocationURNs.includes(location[0]));
  const showDivider = !_.isEmpty(selectedLocations) && !_.isEmpty(filteredLocations);

  const handleOpenErrorDialog = useErrorStore((state) => state.handleOpenErrorDialog);

  const lang = i18n.languages[0];

  let typingTimer: ReturnType<typeof setTimeout>;

  /**
   * Location search functionality
   */

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLocationSearch(event.target.value);
  };

  const handleSearchLocations = async () => {
    if (locationSearch !== '') {
      setLocationsLoading(true);

      const res = await getLinkedinLocationsWithCookies(locationSearch, lang, liAtCookie, sessionCookie);

      if (!res.error) {
        const resLocations = res || [];
        setLocations(_.take(resLocations, 5));
      } else {
        setLocations([]);
        handleOpenErrorDialog(res?.error, '/linkedin/lookups/geo', 'post');
      }
      setLocationsLoading(false);
    } else {
      setLocations([]);
    }
  };

  const searchKeyUp = () => {
    clearTimeout(typingTimer);
    typingTimer = setTimeout(() => {
      handleSearchLocations();
    }, 500);
  };

  const searchKeyDown = () => {
    clearTimeout(typingTimer);
  };

  const handleMultiSelectLocationsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newLocation = event.target.value;
    const updateSelectedLocations = [...selectedLocations];

    if (selectedLocationURNs.includes(newLocation)) {
      const findSelected = updateSelectedLocations.find((location) => location[0] === newLocation);

      if (!_.isNil(findSelected)) {
        _.pull(updateSelectedLocations, findSelected);
      }
    } else {
      const findLocation = locations.find((location) => location[0] === newLocation);

      if (!_.isNil(findLocation)) {
        updateSelectedLocations.push(findLocation);
      }
    }

    setSelectedLocations(updateSelectedLocations);
  };

  /**
   * Job title search handlers
   */

  const handleSearchJobTitles = async () => {
    setJobTitlesLoading(true);

    const res = await getJobTitleSuggestions(occupation, text, selectedLocationURNs, liAtCookie, sessionCookie);

    if (!res.error) {
      const resJobTitles = res.jobTitleSuggestions;

      setJobTitles(resJobTitles);
    } else {
      handleOpenErrorDialog(res?.error, '/linkedin/lookups/job-titles', 'post');
    }

    setJobTitlesLoading(false);
  };

  return (
    <Dialog classes={{ paper: classes.paper }} open={open} fullWidth={true} maxWidth={_.isEmpty(jobTitles) ? 'xs' : 'sm'}>
      <DialogTitle className={classes.title}>
        <Typography className={classes.titleText}>{t('similarJobTitles')}</Typography>
      </DialogTitle>
      <DialogContent className={cx(classes.content, !_.isEmpty(jobTitles) ? 'table' : null)}>
        <Stack gap={2.5}>
          {jobTitlesLoading ? (
            <PaiDotLoader text={t('similarJobTitlesSearchText')} />
          ) : _.isEmpty(jobTitles) ? (
            <>
              <PaiTextField
                placeholder={t('enterLocation')}
                label={t('enterLocationLabel')}
                value={locationSearch}
                onChange={handleSearchChange}
                onKeyUp={searchKeyUp}
                onKeyDown={searchKeyDown}
                loading={locationsLoading}
              />
              <Stack gap={0.5}>
                {!_.isEmpty(locations) ? (
                  <>
                    {filteredLocations.map((location) => (
                      <PaiCheckbox
                        key={location[0]}
                        label={location[1]}
                        value={location[0]}
                        checked={selectedLocationURNs.includes(location[0])}
                        onChange={handleMultiSelectLocationsChange}
                      />
                    ))}
                    {showDivider ? <Divider sx={{ margin: '8px 0' }} /> : null}
                  </>
                ) : null}
                {!_.isEmpty(selectedLocations) ? (
                  <>
                    {selectedLocations.map((location) => (
                      <PaiCheckbox
                        key={location[0]}
                        label={location[1]}
                        value={location[0]}
                        checked={selectedLocationURNs.includes(location[0])}
                        onChange={handleMultiSelectLocationsChange}
                      />
                    ))}
                  </>
                ) : null}
              </Stack>
              {!_.isEmpty(selectedLocations) ? (
                <Stack alignItems={'center'}>
                  <PaiButton text={t('startSearch')} action={handleSearchJobTitles} />
                </Stack>
              ) : null}
            </>
          ) : (
            <JobTitlesTable jobTitles={jobTitles} />
          )}
        </Stack>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <PaiLink text={t('close')} size={'sm'} underline={'always'} action={handleClose} />
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: 0,
  },
  title: {
    background: theme.palette.primary.main,
  },
  titleText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#FFF',
    textAlign: 'center',
  },
  content: {
    padding: '20px 24px !important',
    '&.table': {
      padding: '20px 12px !important',
    },
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '0 24px 20px 24px',
  },
}));

export default JobTitleSuggestionsDialog;
